import { type FunctionComponent } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { type questionListLayout_viewer$data } from './__generated__/questionListLayout_viewer.graphql';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { PageLayout } from 'dibs-elements/exports/PageLayout';
import { Link } from 'dibs-elements/exports/Link';
import { useServerVarsContext } from '../../sharedComponents/ServerVarsContext';
import { PageLinks } from 'dibs-page-links/exports/PageLinks';
import { PAGINATION_SIZES } from '../constants';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

import styles from './questionListLayout.scss';

//Components
import { QuestionListTop } from './questionListTop/QuestionListTop';

type Props = {
    viewer?: questionListLayout_viewer$data;
    page: number;
};

const QuestionListLayoutComponent: FunctionComponent<Props> = ({ viewer, page }) => {
    const { isMobile } = useServerVarsContext();

    const handlePagination = ({ pageNumber }: { pageNumber: number }): void => {
        window.location.href = handleLocaleUrl(
            `/answers/page/${pageNumber}/`,
            GLOBAL_CLIENT_ONLY_LOCALE
        );
    };

    const questions = viewer?.questions?.edges?.filter(filterNulls) || [];
    const numberOfPages = viewer?.questions?.numberOfPages || 1;

    if (questions.length) {
        return (
            <PageLayout setViewportBackground setOffset={!isMobile}>
                <div className={styles.container}>
                    <QuestionListTop />
                    <div className={styles.wrapper}>
                        <ul className={styles.questionList}>
                            {questions?.map(({ node: question }) => {
                                if (question) {
                                    const { url, title } = question;
                                    if (url && title) {
                                        return (
                                            <li className={styles.question} key={url}>
                                                <Link
                                                    className={styles.questionLink}
                                                    href={url}
                                                    dataTn="question-link"
                                                >
                                                    {title}
                                                </Link>
                                            </li>
                                        );
                                    }
                                }
                                return null;
                            })}
                        </ul>
                        <PageLinks
                            numberOfPages={numberOfPages}
                            currentPage={page}
                            onPageChange={handlePagination}
                            totalPageLinks={
                                isMobile ? PAGINATION_SIZES.MOBILE : PAGINATION_SIZES.DESKTOP
                            }
                        />
                    </div>
                </div>
            </PageLayout>
        );
    }

    return null;
};

export const QuestionListLayout = createFragmentContainer(QuestionListLayoutComponent, {
    viewer: graphql`
        fragment questionListLayout_viewer on Viewer {
            questions(first: $first, page: $page, minAnswersCount: 1) {
                totalResults
                numberOfPages
                edges {
                    node {
                        url
                        title
                    }
                }
            }
        }
    `,
});
